/**
 * T016 — 效能榜单 + 私有预设（模块十 4.5）
 *
 * 场景 10.1: 仅合规记录；0 值不上榜；Admin 不作为一行；Manager 受 scope；Admin 全局。
 * 场景 10.2: 计入任务过滤 + 私有预设（默认全部任务；勾选即时重算；标题下显示口径；预设私有）。
 * 场景 10.3: 快照拆分（窗内跨组 → 拆行；前三 🥇🥈🥉；组长 🎖）。
 */

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

// ── Types ──────────────────────────────────────────────────────────────────────

interface LeaderboardRow {
  rank: number
  medal: string | null
  member_id: string
  display_name: string
  is_group_lead: boolean
  region_code: string
  group_name: string
  volume: number
  hours: string
  record_count: number
}

interface LeaderboardData {
  leaderboard: LeaderboardRow[]
  range: { from: string; to: string }
  task_filter: { active: boolean; included_task_ids: string[] }
}

interface TaskItem {
  id: string
  name: string
  category_code: string
  is_active: boolean
  deleted_at: string | null
}

interface CategoryItem {
  id: string
  code: string
  display_name_en: string
  display_name_cn: string
  tasks: TaskItem[]
}

interface Preset {
  id: string
  name: string
  included_task_ids: string[]
  created_at: string
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildLeaderboardUrl(
  tr: TimeRangeValue,
  includedTaskIds: string[] | null,
): string {
  const params = new URLSearchParams({ range: tr.range })
  if (tr.range === 'custom') {
    if (tr.from) params.set('from', tr.from)
    if (tr.to) params.set('to', tr.to)
  }
  if (includedTaskIds !== null) {
    params.set('included_task_ids', includedTaskIds.join(','))
  }
  return `/api/leaderboard?${params.toString()}`
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function LeaderboardPage() {
  const { t } = useLang()
  const { accessToken, user } = useAuth()
  const role = user?.role ?? ''

  // Time range
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(defaultTimeRange())

  // Task filter state: null = no filter (all tasks), list = specific task IDs
  const [allCategories, setAllCategories] = useState<CategoryItem[]>([])
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set())
  const [filterActive, setFilterActive] = useState(false)
  const [showFilterPanel, setShowFilterPanel] = useState(false)

  // Presets
  const [presets, setPresets] = useState<Preset[]>([])
  const [newPresetName, setNewPresetName] = useState('')
  const [showPresetPanel, setShowPresetPanel] = useState(false)
  const [presetMsg, setPresetMsg] = useState<string | null>(null)

  // Leaderboard data
  const [data, setData] = useState<LeaderboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch all active tasks for the filter tree (once)
  useEffect(() => {
    if (!accessToken || role === 'member') return
    fetch('/api/categories', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => res.json())
      .then((resp: { data: CategoryItem[] }) => {
        const cats = resp.data ?? []
        setAllCategories(cats)
        // Initialize selectedTaskIds with all active task IDs
        const allIds = new Set(
          cats.flatMap((c) => c.tasks.filter((t) => t.is_active && !t.deleted_at).map((t) => t.id))
        )
        setSelectedTaskIds(allIds)
      })
      .catch(() => {})
  }, [role, accessToken])

  // Fetch presets
  const loadPresets = useCallback(() => {
    if (!accessToken || role === 'member') return
    fetch('/api/leaderboard-presets', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => res.json())
      .then((d) => setPresets(d.data ?? []))
      .catch(() => {})
  }, [role, accessToken])

  useEffect(() => {
    loadPresets()
  }, [loadPresets])

  // Fetch leaderboard data
  useEffect(() => {
    if (!accessToken || role === 'member') return
    const included = filterActive ? Array.from(selectedTaskIds) : null
    setLoading(true)
    setError(null)
    fetch(buildLeaderboardUrl(timeRange, included), { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message ?? `HTTP ${res.status}`)
        }
        return res.json() as Promise<LeaderboardData>
      })
      .then((d) => {
        setData(d)
        setLoading(false)
      })
      .catch((err: Error) => {
        setError(err.message)
        setLoading(false)
      })
  }, [timeRange, selectedTaskIds, filterActive, role, accessToken])

  if (role === 'member') {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--danger)', fontWeight: 500 }}>{t('leaderboardForbidden')}</p>
      </div>
    )
  }

  // ── Computed values for scope descriptor ─────────────────────────────────────

  const allActiveTasks = allCategories.flatMap((c) =>
    c.tasks.filter((t) => t.is_active && !t.deleted_at)
  )
  const totalTasks = allActiveTasks.length
  const includedCount = filterActive ? selectedTaskIds.size : totalTasks
  const excludedTasks = filterActive
    ? allActiveTasks.filter((t) => !selectedTaskIds.has(t.id))
    : []

  // Scope descriptor label
  const rangeLabel = {
    this_week: t('timeRangeThisWeek'),
    this_month: t('timeRangeThisMonth'),
    last_month: t('timeRangeLastMonth'),
    this_quarter: t('timeRangeThisQuarter'),
    custom: `${timeRange.from ?? ''} ~ ${timeRange.to ?? ''}`,
  }[timeRange.range]

  const scopeDescriptor = `${rangeLabel} · ${includedCount}/${totalTasks} ${t('leaderboardTasksIncluded')}`

  // ── Task toggle handlers ──────────────────────────────────────────────────────

  const handleTaskToggle = (taskId: string) => {
    setFilterActive(true)
    setSelectedTaskIds((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) {
        next.delete(taskId)
      } else {
        next.add(taskId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    const allIds = new Set(allActiveTasks.map((t) => t.id))
    setSelectedTaskIds(allIds)
    setFilterActive(false)
  }

  const handleDeselectAll = () => {
    setSelectedTaskIds(new Set())
    setFilterActive(true)
  }

  // ── Preset handlers ───────────────────────────────────────────────────────────

  const handleSavePreset = async () => {
    if (!newPresetName.trim() || !accessToken) return
    const resp = await fetch('/api/leaderboard-presets', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: newPresetName.trim(),
        included_task_ids: Array.from(selectedTaskIds),
      }),
    })
    if (resp.ok) {
      setNewPresetName('')
      setPresetMsg(t('leaderboardPresetSaved'))
      loadPresets()
      setTimeout(() => setPresetMsg(null), 2000)
    }
  }

  const handleLoadPreset = (preset: Preset) => {
    setSelectedTaskIds(new Set(preset.included_task_ids))
    setFilterActive(true)
    setShowPresetPanel(false)
  }

  const handleDeletePreset = async (presetId: string) => {
    if (!accessToken) return
    await fetch(`/api/leaderboard-presets/${presetId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    loadPresets()
  }

  const board = data?.leaderboard ?? []

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <h2 style={{ marginBottom: 4, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
        {t('navLeaderboard')}
      </h2>

      {/* Scope descriptor */}
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 8px', fontWeight: 500 }}>
          {scopeDescriptor}
        </span>
        {excludedTasks.map((t) => (
          <span
            key={t.id}
            style={{
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 12,
              color: 'var(--danger)',
              cursor: 'pointer',
            }}
            onClick={() => handleTaskToggle(t.id)}
            title={t.name + ' (click to re-include)'}
          >
            ✕ {t.name}
          </span>
        ))}
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <TimeRange value={timeRange} onChange={(tr) => setTimeRange(tr)} />

        {/* Task filter toggle */}
        <button
          onClick={() => setShowFilterPanel((v) => !v)}
          style={{
            padding: '5px 12px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            background: filterActive ? 'var(--accent)' : 'var(--bg-surface)',
            color: filterActive ? 'var(--accent-contrast)' : 'var(--text-primary)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {t('leaderboardTaskFilter')} {filterActive ? `(${includedCount}/${totalTasks})` : ''}
        </button>

        {/* Preset panel toggle */}
        <button
          onClick={() => setShowPresetPanel((v) => !v)}
          style={{
            padding: '5px 12px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {t('leaderboardPresets')} ({presets.length})
        </button>
      </div>

      {/* Task filter panel */}
      {showFilterPanel && (
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={handleSelectAll}
              style={{ padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
            >
              {t('leaderboardSelectAll')}
            </button>
            <button
              onClick={handleDeselectAll}
              style={{ padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
            >
              {t('leaderboardDeselectAll')}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            {allCategories.map((cat) => {
              const activeTasks = cat.tasks.filter((t) => t.is_active && !t.deleted_at)
              if (activeTasks.length === 0) return null
              return (
                <div key={cat.code} style={{ minWidth: 180 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: 'var(--text-primary)' }}>
                    {cat.display_name_en}
                  </div>
                  {activeTasks.map((task) => (
                    <label
                      key={task.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4, cursor: 'pointer', color: 'var(--text-primary)' }}
                    >
                      <input
                        type="checkbox"
                        checked={!filterActive || selectedTaskIds.has(task.id)}
                        onChange={() => handleTaskToggle(task.id)}
                      />
                      {task.name}
                    </label>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Preset panel */}
      {showPresetPanel && (
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--text-primary)' }}>{t('leaderboardPresets')}</div>

          {/* Save current filter as preset */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <input
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              placeholder={t('leaderboardPresetNamePlaceholder')}
              style={{ flex: 1, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset() }}
            />
            <button
              onClick={handleSavePreset}
              disabled={!newPresetName.trim()}
              style={{
                padding: '5px 12px',
                background: 'var(--accent)',
                color: 'var(--accent-contrast)',
                border: 'none',
                borderRadius: 4,
                fontSize: 13,
                cursor: newPresetName.trim() ? 'pointer' : 'not-allowed',
                opacity: newPresetName.trim() ? 1 : 0.5,
              }}
            >
              {t('leaderboardPresetSave')}
            </button>
          </div>
          {presetMsg && (
            <div style={{ color: 'var(--success)', fontSize: 13, marginBottom: 8 }}>{presetMsg}</div>
          )}

          {/* Preset list */}
          {presets.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('leaderboardNoPresets')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {presets.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    padding: '6px 10px',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>
                    {p.name}
                    <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>
                      ({p.included_task_ids.length} {t('leaderboardTasksIncluded')})
                    </span>
                  </span>
                  <button
                    onClick={() => handleLoadPreset(p)}
                    style={{ padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                  >
                    {t('leaderboardPresetLoad')}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('leaderboardPresetDeleteConfirm'))) {
                        handleDeletePreset(p.id)
                      }
                    }}
                    style={{ padding: '3px 8px', border: '1px solid var(--danger)', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)' }}
                  >
                    {t('leaderboardPresetDelete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loading / error */}
      {loading && <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loading')}</div>}
      {error && <div style={{ color: 'var(--danger)', padding: '8px 0' }}>{error}</div>}

      {/* Leaderboard table */}
      {!loading && data && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {[
                  t('leaderboardColRank'),
                  t('leaderboardColMember'),
                  t('leaderboardColRegion'),
                  t('leaderboardColGroup'),
                  t('leaderboardColVolume'),
                  t('leaderboardColHours'),
                  t('leaderboardColRecords'),
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {board.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '32px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {t('leaderboardNoData')}
                  </td>
                </tr>
              ) : (
                board.map((row) => {
                  const isTop3 = row.rank <= 3
                  const rowBg = isTop3 && row.rank === 1 ? 'var(--warning-bg)' : 'var(--bg-surface)'
                  return (
                    <tr
                      key={`${row.member_id}-${row.group_name}`}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        background: rowBg,
                      }}
                      onMouseEnter={(e) => {
                        ;(e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-elevated)'
                      }}
                      onMouseLeave={(e) => {
                        ;(e.currentTarget as HTMLTableRowElement).style.background = rowBg
                      }}
                    >
                      {/* Rank */}
                      <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: row.medal ? 18 : 13, whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>
                        {row.medal ? row.medal : `#${row.rank}`}
                      </td>

                      {/* Member name + group lead badge */}
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--text-primary)' }}>
                        {row.display_name}
                        {row.is_group_lead && (
                          <span style={{ marginLeft: 4, fontSize: 14 }} title={t('leaderboardGroupLead')}>
                            🎖
                          </span>
                        )}
                      </td>

                      {/* Region */}
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{row.region_code}</td>

                      {/* Group */}
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{row.group_name}</td>

                      {/* Volume */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {row.volume.toLocaleString()}
                      </td>

                      {/* Hours */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                        {row.hours}h
                      </td>

                      {/* Records */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)' }}>
                        {row.record_count}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
